// Tracking Battery Guard settings card. Foreground-only enforcement.
// This guard protects ROUTE TRACKING (not uploads). Mounted on the Tracking
// screen and on Offline Controls so it's discoverable both places.
import { useEffect, useState } from "react";
import { BatteryLow, ShieldAlert, Check, ChevronDown, ChevronRight } from "lucide-react";

import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  BATTERY_GUARD_THRESHOLDS,
  type BatteryGuardMode,
  getBatteryGuardMode,
  setBatteryGuardMode,
  getBatteryGuardThresholdPct,
  setBatteryGuardThresholdPct,
  getTrackingAllowWhenCharging,
  setTrackingAllowWhenCharging,
} from "@/lib/prefs";
import { getBatteryInfo, type BatteryInfo } from "@/lib/native/battery";

const MODES: Array<{ value: BatteryGuardMode; label: string; desc: string }> = [
  { value: "off",   label: "Off",            desc: "Do nothing." },
  { value: "warn",  label: "Warn me",        desc: "Show a notice when battery is low." },
  { value: "pause", label: "Pause tracking", desc: "Stop tracking automatically when low." },
];

export function BatteryGuardCard() {
  const [mode, setMode] = useState<BatteryGuardMode>(() => getBatteryGuardMode());
  const [threshold, setThreshold] = useState<number>(() => getBatteryGuardThresholdPct());
  const [allowCharging, setAllowCharging] = useState<boolean>(() => getTrackingAllowWhenCharging());
  const [battery, setBattery] = useState<BatteryInfo>({ level: null, isCharging: null });
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const info = await getBatteryInfo();
        if (alive) setBattery(info);
      } catch { /* ignore */ }
    };
    void refresh();
    const id = window.setInterval(refresh, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  function pickMode(m: BatteryGuardMode) {
    setMode(m);
    setBatteryGuardMode(m);
    toast.success("Saved on this device");
  }
  function pickThreshold(n: number) {
    setThreshold(n);
    setBatteryGuardThresholdPct(n);
    toast.success("Saved on this device");
  }
  function pickAllowCharging(v: boolean) {
    setAllowCharging(v);
    setTrackingAllowWhenCharging(v);
    toast.success("Saved on this device");
  }

  const pct = battery.level === null ? null : Math.round(battery.level * 100);
  const charging = battery.isCharging === true;
  const guardActive = mode !== "off" && pct !== null && pct <= threshold;
  const chargingBypass = guardActive && charging && allowCharging;
  const isLow = guardActive && !chargingBypass;

  return (
    <div
      className={`mt-4 rounded-2xl border bg-card p-4 text-sm ${
        isLow ? "border-destructive/40" : "border-border"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 text-left"
      >
        {isLow ? (
          <BatteryLow className="size-4 text-destructive" />
        ) : (
          <ShieldAlert className="size-4 text-muted-foreground" />
        )}
        <p className="font-semibold">Tracking Battery Guard</p>
        <span
          className={`ml-auto text-xs ${
            isLow ? "text-destructive font-medium" : "text-muted-foreground"
          }`}
        >
          {MODES.find((m) => m.value === mode)?.label ?? "Off"}
          {mode !== "off" ? ` · ${threshold}%` : ""}
        </span>
        {open
          ? <ChevronDown className="size-4 text-muted-foreground" />
          : <ChevronRight className="size-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="mt-3">
          {pct !== null && (
            <p
              className={`text-xs mb-2 ${
                isLow ? "text-destructive font-medium" : "text-muted-foreground"
              }`}
            >
              Battery {pct}%{charging ? " · charging" : ""}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Pause route tracking when battery is below this level, unless charging is allowed.
            This is separate from Upload rules — it only affects GPS tracking, not uploads.
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Saved on this device. Doesn't need internet.
          </p>

          <div className="mt-3 grid gap-2">
            {MODES.map((m) => {
              const active = mode === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => pickMode(m.value)}
                  aria-pressed={active}
                  className={`flex items-start justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition ${
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card hover:bg-accent/30"
                  }`}
                >
                  <div>
                    <p className="text-sm font-semibold">{m.label}</p>
                    <p
                      className={`text-[11px] mt-0.5 ${
                        active ? "text-primary-foreground/80" : "text-muted-foreground"
                      }`}
                    >
                      {m.desc}
                    </p>
                  </div>
                  <span
                    className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border ${
                      active
                        ? "border-primary-foreground/70 bg-primary-foreground text-primary"
                        : "border-border bg-muted/40 text-transparent"
                    }`}
                    aria-hidden="true"
                  >
                    <Check className="size-3.5" strokeWidth={3} />
                  </span>
                </button>
              );
            })}
          </div>

          <div className={`mt-4 ${mode === "off" ? "opacity-50" : ""}`}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Trigger at
            </p>
            <div className="grid grid-cols-4 gap-1.5">
              {BATTERY_GUARD_THRESHOLDS.map((n) => {
                const active = threshold === n;
                return (
                  <button
                    key={n}
                    type="button"
                    disabled={mode === "off"}
                    onClick={() => pickThreshold(n)}
                    aria-pressed={active}
                    className={`rounded-lg border px-2 py-2 text-xs font-semibold transition ${
                      active
                        ? "border-primary bg-primary text-primary-foreground shadow-sm"
                        : "border-border bg-card hover:bg-accent/30 text-muted-foreground"
                    } ${mode === "off" ? "cursor-not-allowed" : ""}`}
                  >
                    {n}%{n === 20 ? "★" : ""}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              ★ 20% is recommended. Background enforcement depends on iOS and is planned for a future native update.
            </p>
          </div>

          <div className={`mt-4 flex items-start justify-between gap-3 rounded-xl border border-border bg-card px-3 py-2.5 ${mode === "off" ? "opacity-50" : ""}`}>
            <div className="flex-1">
              <p className="text-sm font-medium">Allow when charging</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Keep tracking even below the threshold while the device is plugged in.
              </p>
            </div>
            <Switch
              disabled={mode === "off"}
              checked={allowCharging}
              onCheckedChange={pickAllowCharging}
            />
          </div>
          {chargingBypass && (
            <p className="text-[11px] text-muted-foreground mt-2">
              Charging — guard bypassed.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
